import { Theme } from '@mui/material';
import { LabelAssignment } from '@provision/entity-types';
import { NON_LABEL_GROUP_ID } from 'src/constants/label';
import { CUSTOM_VIEW_GROUPS } from 'src/graphql/entities/custom-view/constants';
import {
  CustomViewAttribute,
  UIType,
} from 'src/graphql/entities/custom-view/custom-view.types';
import { pickPastelColor } from 'src/utils/strings';
import { SiteAttribute, SiteAttributeAccessors, SiteAttributeType } from './constants';

export const siteAttributeConfigList: CustomViewAttribute<SiteAttributeType>[] = [
  {
    attribute: SiteAttribute.siteClientSegment,
    attributeLabel: 'Client Segment',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Site,
    default: true,
  },
  {
    attribute: SiteAttribute.siteName,
    attributeLabel: 'Site Name',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Site,
    default: true,
  },
  {
    attribute: SiteAttribute.clientName,
    attributeLabel: 'Client Name',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Client,
    default: true,
  },
  {
    attribute: SiteAttribute.clientActivatedMembership,
    attributeLabel: 'Service Level',
    uiType: UIType.Custom,
    group: CUSTOM_VIEW_GROUPS.Site,
    default: true,
  },
  {
    attribute: SiteAttribute.ownerName,
    attributeLabel: 'Account Manager',
    uiType: UIType.Avatar,
    group: CUSTOM_VIEW_GROUPS.Site,
    default: true,
  },
  {
    attribute: SiteAttribute.createdAt,
    attributeLabel: 'Created At',
    uiType: UIType.Date,
    group: CUSTOM_VIEW_GROUPS.Site,
    default: true,
  },
  {
    attribute: SiteAttribute.updatedAt,
    attributeLabel: 'Updated At',
    uiType: UIType.Date,
    group: CUSTOM_VIEW_GROUPS.Site,
    default: true,
  },
  {
    attribute: SiteAttribute.id,
    attributeLabel: 'Site ID',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Site,
  },
  {
    attribute: SiteAttribute.siteAddress,
    attributeLabel: 'Address',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Site,
  },
  {
    group: CUSTOM_VIEW_GROUPS.Site,
    attribute: SiteAttribute.city,
    attributeLabel: 'City',
    uiType: UIType.Text,
  },
  {
    group: CUSTOM_VIEW_GROUPS.Site,
    attribute: SiteAttribute.state,
    attributeLabel: 'State',
    uiType: UIType.Text,
  },
  {
    group: CUSTOM_VIEW_GROUPS.Site,
    attribute: SiteAttribute.businessName,
    attributeLabel: 'Business Name',
    uiType: UIType.Text,
  },
  {
    group: CUSTOM_VIEW_GROUPS.Site,
    attribute: SiteAttribute.zip,
    attributeLabel: 'Zip/Postal Code',
    uiType: UIType.Text,
  },
  {
    group: CUSTOM_VIEW_GROUPS.Site,
    attribute: SiteAttribute.cppExpDate,
    attributeLabel: 'CPP Expiration',
    uiType: UIType.Date,
  },
  {
    attribute: SiteAttribute.siteChargifySubId,
    attributeLabel: 'Subscription ID',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Site,
  },
  {
    attribute: SiteAttribute.siteTOS,
    attributeLabel: 'TOS',
    uiType: UIType.Boolean,
    group: CUSTOM_VIEW_GROUPS.Site,
  },
  {
    group: CUSTOM_VIEW_GROUPS.Site,
    attribute: SiteAttribute.membershipRegistered,
    attributeLabel: 'Membership Registered',
    uiType: UIType.Boolean,
  },
  {
    attribute: SiteAttribute.isMonitored,
    attributeLabel: 'Is Monitored',
    uiType: UIType.Boolean,
    group: CUSTOM_VIEW_GROUPS.Site,
  },
  {
    attribute: SiteAttribute.partnerName,
    attributeLabel: 'Partner Name',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Partner,
  },
  {
    attribute: SiteAttribute.timeZone,
    attributeLabel: 'Timezone',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Partner,
  },
  {
    attribute: SiteAttribute.clientPhone,
    attributeLabel: 'Client Phone',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Client,
  },
  {
    attribute: SiteAttribute.clientEmail,
    attributeLabel: 'Client Email',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Client,
  },

  {
    attribute: SiteAttribute.projectId,
    attributeLabel: 'Project ID',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Project,
  },
  {
    attribute: SiteAttribute.projectName,
    attributeLabel: 'Project Name',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Project,
  },
  {
    attribute: SiteAttribute.projectStage,
    attributeLabel: 'Project Stage',
    uiType: UIType.Text,
    group: CUSTOM_VIEW_GROUPS.Project,
  },
  {
    attribute: SiteAttribute.projectEstDay1,
    attributeLabel: 'Estimated Day1',
    uiType: UIType.Date,
    group: CUSTOM_VIEW_GROUPS.Project,
  },
  {
    attribute: SiteAttribute.projectDay1StartDate,
    attributeLabel: 'Day1 Start Date',
    uiType: UIType.Date,
    group: CUSTOM_VIEW_GROUPS.Project,
  },
  {
    attribute: SiteAttribute.projectWarrantyExpiration,
    attributeLabel: 'Warranty Expiration',
    uiType: UIType.Date,
    group: CUSTOM_VIEW_GROUPS.Project,
  },
  {
    group: CUSTOM_VIEW_GROUPS.Site,
    attribute: SiteAttribute.siteLabel,
    attributeLabel: 'Labels',
    uiType: UIType.Chips,
    getBackgroundColor: (value: unknown, theme: Theme): string => {
      const labelAssignment = value as LabelAssignment;
      if (!labelAssignment?.label) return '';
      return pickPastelColor(
        labelAssignment.label.labelGroupId ?? NON_LABEL_GROUP_ID,
        theme.palette.mode,
      );
    },
    renderValue: (value: unknown) => {
      const labelAssignment = value as LabelAssignment;
      return labelAssignment?.label?.displayName ?? '';
    },
  },
].map((i) => ({
  ...i,
  accessor: SiteAttributeAccessors[i.attribute].display,
  sortingKey: SiteAttributeAccessors[i.attribute].sort,
  sortingAggFunction: SiteAttributeAccessors[i.attribute].aggFunction,
}));

export const siteAttributeConfigMap = new Map(
  siteAttributeConfigList.map((i) => [i.attribute, i]),
);
